import React, { useState, useRef, useEffect, useCallback, useMemo, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { runWithConflictConfirm } from './conflictConfirm';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { MeetingFull, ReminderOut, WeatherForecastPeriod } from '@/redux/internal-services/workspaceApiExtensions';
import {
  useUpdateMeetingStatusMutation, useCancelMeetingMutation, useRescheduleMeetingMutation,
  useGetMeetingNotesQuery, useAddMeetingNoteMutation,
  useGetRemindersQuery, useCreateReminderMutation, useCancelReminderMutation,
  useGetPropertyDetailQuery, useGetWeatherForecastQuery,
} from '@/redux/internal-services/workspaceApiExtensions';
import { useGetEngagementByIdQuery } from '@/redux/internal-services/workflowEngineApi';
import { IcoCalendar, ContextTypeIcon, ENTITY_ROUTE } from '@/components/Tasks/TaskCard';
import { useSamSurfaceContext } from '@/hooks';
import { SwipeHint, ExpandChevron } from '@/components/UI/SwipeHint';

// Portalled menus use inline styles, so they take the numeric value of Tailwind's `z-popover`.
const POPOVER_Z = 70;

// ─── Weather (job-site forecast for the meeting's date, when linked to a property) ─────────────

/** The one hourly period whose [startTime, endTime) window contains the meeting's start —
 *  meetings are short (~30-60min) so a single NWS hourly period is enough context. Meetings
 *  outside NWS's ~7-day forecast window (past or far future) simply have no matching period. */
function findPeriodForMeeting(periods: WeatherForecastPeriod[] | undefined, startTimeIso: string): WeatherForecastPeriod | null {
  if (!periods?.length) return null;
  const target = new Date(startTimeIso).getTime();
  return periods.find(p => {
    const start = new Date(p.startTime).getTime();
    const end = new Date(p.endTime).getTime();
    return target >= start && target < end;
  }) ?? null;
}

function weatherEmoji(shortForecast: string): string {
  const s = shortForecast.toLowerCase();
  if (s.includes('thunder') || s.includes('storm')) return '⛈️';
  if (s.includes('snow') || s.includes('flurries') || s.includes('sleet') || s.includes('ice')) return '🌨️';
  if (s.includes('rain') || s.includes('shower') || s.includes('drizzle')) return '🌧️';
  if (s.includes('fog') || s.includes('haze')) return '🌫️';
  if (s.includes('wind')) return '💨';
  if (s.includes('cloud') || s.includes('overcast')) return '☁️';
  if (s.includes('partly') || s.includes('mostly')) return '⛅';
  if (s.includes('clear') || s.includes('sunny')) return '☀️';
  return '🌡️';
}

/** Resolves the property behind a meeting's context (directly, or via its engagement) and
 *  looks up the forecast for the meeting's start time. Returns null until a property with
 *  resolved coordinates is found — callers render nothing in that case. */
function useMeetingWeather(meeting: MeetingFull): WeatherForecastPeriod | null {
  const ctx = meeting.context;
  const isEngagementCtx = ctx?.context_type === 'engagement';
  const isPropertyCtx = ctx?.context_type === 'property';

  const { data: ctxEngagement } = useGetEngagementByIdQuery(
    ctx?.entity_id as string, { skip: !isEngagementCtx || !ctx?.entity_id },
  );
  const propertyId = isPropertyCtx ? ctx?.entity_id : ctxEngagement?.property_id;

  const { data: property } = useGetPropertyDetailQuery(propertyId as string, { skip: !propertyId });
  const lat = property?.address?.latitude;
  const lon = property?.address?.longitude;

  const { data: forecast } = useGetWeatherForecastQuery(
    { jobId: propertyId as string, lat: lat as number, lon: lon as number, siteLabel: property?.display_address },
    { skip: !propertyId || lat == null || lon == null },
  );

  return useMemo(() => findPeriodForMeeting(forecast?.periods, meeting.start_time), [forecast, meeting.start_time]);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

const AVATAR_COLORS = [
  'bg-accent/15 text-accent-text', 'bg-accent/15 text-accent-text', 'bg-success/15 text-success-text',
  'bg-warning/15 text-warning-text', 'bg-accent/15 text-accent-text', 'bg-success/15 text-success-text',
];

// ─── Config ────────────────────────────────────────────────────────────────────

const STATUS_CFG = {
  scheduled:   { bg: 'bg-accent/10',  text: 'text-accent-text',  label: 'Scheduled',   bar: 'bg-accent/25' },
  rescheduled: { bg: 'bg-warning/10', text: 'text-warning-text', label: 'Rescheduled', bar: 'bg-warning' },
  completed:   { bg: 'bg-ui-hover', text: 'text-ui-text2',  label: 'Completed',   bar: 'bg-ui-border3' },
  cancelled:   { bg: 'bg-ui-hover', text: 'text-ui-text2',  label: 'Cancelled',   bar: 'bg-ui-border3' },
  no_show:     { bg: 'bg-ui-hover', text: 'text-ui-text2',  label: 'No Show',     bar: 'bg-ui-border3' },
} as const;


const THRESHOLD = 90;
const ACTIONABLE = new Set(['scheduled', 'rescheduled']);

const NOTE_CATEGORIES = [
  { value: 'general',              label: 'General' },
  { value: 'customer_requirement', label: 'Customer Req.' },
  { value: 'decision',             label: 'Decision' },
  { value: 'follow_up',            label: 'Follow Up' },
  { value: 'measurement',          label: 'Measurement' },
  { value: 'risk',                 label: 'Risk' },
  { value: 'scope_change',         label: 'Scope Change' },
  { value: 'site_condition',       label: 'Site Condition' },
];

// ─── Meeting time formatting (mirrors TaskCard's due-date chip) ────────────────

type MeetingTimeDisplay = { main: string; rel?: string; isPast: boolean };

function getMeetingTimeDisplay(start: Date, status: string): MeetingTimeDisplay {
  const now = new Date();
  const timeStr = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayDiff = Math.round((startDay.getTime() - todayDay.getTime()) / 86400000);
  const isPast = start < now && !['completed', 'cancelled'].includes(status);

  if (isPast) {
    const diffMs = now.getTime() - start.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffH = Math.round(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    const rel = diffMin < 60 ? `${diffMin}m ago` : diffH < 24 ? `${diffH}h ago` : `${diffD} day${diffD !== 1 ? 's' : ''} ago`;
    if (dayDiff === 0) return { main: `Was today ${timeStr}`, rel, isPast: true };
    const wd = start.toLocaleDateString(undefined, { weekday: 'short' });
    const mo = start.toLocaleDateString(undefined, { month: 'short' });
    return { main: `Was ${wd} ${start.getDate()} ${mo} ${timeStr}`, rel, isPast: true };
  }

  if (dayDiff === 0) {
    const diffMs = start.getTime() - now.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffH = Math.round(diffMs / 3600000);
    const rel = diffMin < 60 ? `in ${diffMin}m` : `in ${diffH}h`;
    return { main: `Today ${timeStr}`, rel, isPast: false };
  }
  if (dayDiff === 1) return { main: `Tomorrow ${timeStr}`, isPast: false };
  const wd = start.toLocaleDateString(undefined, { weekday: 'short' });
  const mo = start.toLocaleDateString(undefined, { month: 'short' });
  return { main: `${wd} ${start.getDate()} ${mo} ${timeStr}`, isPast: false };
}

// ─── Inline expanded detail ────────────────────────────────────────────────────

interface MeetingInlineDetailProps {
  meeting: MeetingFull;
  showReschedule: boolean;
  setShowReschedule: React.Dispatch<React.SetStateAction<boolean>>;
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
}

function MeetingInlineDetail({ meeting, showReschedule, setShowReschedule, onStartNoteTaker }: MeetingInlineDetailProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rescheduleMeeting, { isLoading: rescheduling }] = useRescheduleMeetingMutation();
  const [addNote, { isLoading: addingNote }] = useAddMeetingNoteMutation();
  const [createReminder, { isLoading: creatingReminder }] = useCreateReminderMutation();
  const [cancelReminder] = useCancelReminderMutation();
  const { data: notes = [] } = useGetMeetingNotesQuery(meeting.meeting_id);
  const { data: remindersRes } = useGetRemindersQuery({ source_entity_id: meeting.meeting_id });

  const [noteText, setNoteText] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [rescheduleStart, setRescheduleStart] = useState('');
  const [rescheduleError, setRescheduleError] = useState('');
  const [showReminderPanel, setShowReminderPanel] = useState(false);
  const [reminderDt, setReminderDt] = useState('');
  const [showReminderInput, setShowReminderInput] = useState(false);
  const reminderPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showReminderPanel) return;
    const handler = (e: MouseEvent) => {
      if (reminderPanelRef.current && !reminderPanelRef.current.contains(e.target as Node)) {
        setShowReminderPanel(false);
        setShowReminderInput(false);
        setReminderDt('');
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [showReminderPanel]);

  const reminders = (remindersRes?.data ?? []).filter((r) => ['scheduled', 'snoozed'].includes(r.status));
  const meetingWeather = useMeetingWeather(meeting);

  const ctxHref = meeting.context?.entity_id
    ? (ENTITY_ROUTE[meeting.context.context_type]?.(meeting.context.entity_id) ?? null)
    : null;

  const formatFireTime = (r: ReminderOut) => {
    const fireOn = r.next_fire_on ?? r.dtstart;
    if (!fireOn) return 'Pending';
    const d = new Date(fireOn);
    const now = new Date();
    return d.toDateString() === now.toDateString()
      ? `Today ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
      : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const handleReschedule = async () => {
    if (!rescheduleStart) return;
    const newStart = new Date(rescheduleStart);
    const origStart = meeting.start_time ? new Date(meeting.start_time) : null;
    const origEnd   = meeting.end_time   ? new Date(meeting.end_time)   : null;
    const durationMs = origStart && origEnd ? origEnd.getTime() - origStart.getTime() : 30 * 60 * 1000;
    const newEnd = new Date(newStart.getTime() + durationMs);
    setRescheduleError('');
    try {
      const done = await runWithConflictConfirm((confirm_conflicts) => rescheduleMeeting({
        id: meeting.meeting_id, start_time: newStart.toISOString(), end_time: newEnd.toISOString(), confirm_conflicts,
      }).unwrap(), t);
      if (!done) return;
      setShowReschedule(false);
      setRescheduleStart('');
    } catch (err: unknown) {
      setRescheduleError((err as { data?: { message?: string } })?.data?.message || t('meetings.rescheduleError', 'Failed to reschedule meeting.'));
    }
  };

  const [showNoteForm, setShowNoteForm] = useState(false);

  const isActionable = ACTIONABLE.has(meeting.status);


  return (
    <div className="space-y-1.5" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>

      {/* ── Weather — job-site forecast for the meeting's start time, only when it's linked to
             a property with resolved coordinates (useMeetingWeather returns null otherwise) ─── */}
      {meetingWeather && (
        <div className="flex items-center gap-1.5 px-1.5 py-1 rounded-xl bg-ui-subtle">
          <span className="text-sm leading-none flex-shrink-0">{weatherEmoji(meetingWeather.shortForecast)}</span>
          <span className="text-xs font-semibold text-ui-text flex-shrink-0">
            {meetingWeather.temperature}°{meetingWeather.temperatureUnit}
          </span>
          <span className="text-xs text-ui-text3 truncate">{meetingWeather.shortForecast}</span>
        </div>
      )}

      {/* ── Context link — pill chip, same style as folded view ─── */}
      {meeting.context?.display_label && (
        <div className="flex">
          {ctxHref && isActionable ? (
            <button onClick={() => navigate(ctxHref)} className="inline-flex items-center gap-1 border border-accent/15 bg-accent-soft/20 hover:bg-accent-soft/40 rounded-full px-2 py-0.5 transition-colors max-w-full">
              <ContextTypeIcon type={meeting.context.context_type} />
              <span className="text-xs text-accent-text/70 truncate">{meeting.context.display_label}</span>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-accent-text/40">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 border border-ui-border bg-ui-subtle rounded-full px-2 py-0.5 max-w-full text-ui-text3">
              <ContextTypeIcon type={meeting.context.context_type} />
              <span className="text-xs truncate">{meeting.context.display_label}</span>
            </span>
          )}
        </div>
      )}

      {/* ── Participants ─── */}
      {meeting.participants.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-1">
          {meeting.participants.map((p, i) => {
            const label = p.name || p.email || (p.participant_type === 'internal' ? 'Team member' : 'Unknown');
            return (
              <span
                key={p.participant_id}
                title={p.email ?? undefined}
                className="inline-flex items-center gap-1 rounded-full border border-ui-border pl-0.5 pr-2 py-0.5"
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${isActionable ? AVATAR_COLORS[i % AVATAR_COLORS.length] : 'bg-ui-hover text-ui-text3'}`}>
                  {initials(label)}
                </span>
                <span className="text-xs font-medium text-ui-text truncate max-w-[100px]">{label}</span>
              </span>
            );
          })}
        </div>
      )}

      {/* ── Note Taker ─── */}
      {onStartNoteTaker && isActionable && (
        <button
          type="button"
          onClick={() => onStartNoteTaker('meeting', meeting.meeting_id)}
          className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/5 hover:bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent-text transition"
        >
          <svg viewBox="0 0 24 24" fill="none" className="h-3 w-3 flex-shrink-0" aria-hidden>
            <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z" fill="currentColor" />
            <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Start Note Taker
        </button>
      )}

      {/* ── Reschedule picker ─── */}
      {showReschedule && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <input
              type="datetime-local"
              value={rescheduleStart}
              onChange={e => setRescheduleStart(e.target.value)}
              min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
              className="flex-1 h-6 text-micro border border-ui-border rounded-lg px-2 bg-ui-input text-ui-text focus:outline-none focus:ring-1 focus:ring-ui-ring/30"
            />
            <button
              onClick={handleReschedule}
              disabled={!rescheduleStart || rescheduling}
              className="flex-shrink-0 text-xs px-2.5 h-6 rounded-lg btn-sheen text-on-primary font-semibold disabled:opacity-50 transition-colors"
            >
              {rescheduling ? '…' : 'Set'}
            </button>
          </div>
          {rescheduleError && <p className="text-xs text-danger-text">{rescheduleError}</p>}
        </div>
      )}

      {/* ── Reminders — chips + popup panel ─── */}
      <div className="relative" ref={reminderPanelRef}>
        {/* Chip row */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {reminders.slice(0, 2).map(r => isActionable ? (
            <button key={r.id} type="button"
              onClick={() => setShowReminderPanel(v => !v)}
              className="inline-flex items-center gap-1 rounded-full bg-ui-subtle border border-ui-border px-2 py-0.5 text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className="flex-shrink-0 text-ui-text3">
                <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
              </svg>
              {formatFireTime(r)}
            </button>
          ) : (
            <span key={r.id}
              className="inline-flex items-center gap-1 rounded-full bg-ui-subtle border border-ui-border px-2 py-0.5 text-xs font-medium text-ui-text2">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className="flex-shrink-0 text-ui-text3">
                <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
              </svg>
              {formatFireTime(r)}
            </span>
          ))}
          {reminders.length > 2 && isActionable && (
            <button type="button" onClick={() => setShowReminderPanel(v => !v)}
              className="inline-flex items-center rounded-full bg-ui-subtle border border-ui-border px-2 py-0.5 text-xs font-medium text-ui-text2 hover:bg-ui-hover transition-colors">
              +{reminders.length - 2}
            </button>
          )}
          {reminders.length > 2 && !isActionable && (
            <span className="inline-flex items-center rounded-full bg-ui-subtle border border-ui-border px-2 py-0.5 text-xs font-medium text-ui-text2">
              +{reminders.length - 2}
            </span>
          )}
          {isActionable && reminders.length === 0 && (
            <button type="button" onClick={() => { setShowReminderPanel(true); setShowReminderInput(true); }}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-ui-border3 px-2 py-0.5 text-xs text-ui-text3 hover:text-ui-text hover:border-accent/30 transition-colors">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="flex-shrink-0">
                <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
              </svg>
              Add reminder
            </button>
          )}
          {isActionable && reminders.length > 0 && (
            <button type="button" onClick={() => { setShowReminderPanel(true); setShowReminderInput(true); }}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-dashed border-ui-border3 text-ui-text3 hover:text-ui-text hover:border-accent/30 transition-colors flex-shrink-0">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          )}
        </div>

        {/* Reminder panel */}
        {showReminderPanel && (
          <>
          <div className="fixed inset-0 z-40" onMouseDown={e => { e.stopPropagation(); setShowReminderPanel(false); setShowReminderInput(false); setReminderDt(''); }} />
          <div className="mt-1.5 rounded-xl border border-ui-border bg-ui-card shadow-md overflow-hidden relative z-50">
            {/* Header */}
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-ui-border/70">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className="text-warning-text flex-shrink-0">
                <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
              </svg>
              <span className="text-xs font-semibold text-ui-text">Reminders</span>
              <span className="flex-1"/>
              {reminders.length > 0 && (
                <span className="text-xs text-ui-text3">{reminders.length} set</span>
              )}
            </div>

            {/* Reminder rows */}
            {reminders.map(r => {
              const isRecurring = !!(r as any).rrule;
              return (
                <div key={r.id} className="flex items-center gap-2 px-3 py-2 border-b border-ui-border/40">
                  {isRecurring ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="flex-shrink-0 text-accent-text">
                      <path d="M4 12a8 8 0 0 1 13.7-5.6M20 12a8 8 0 0 1-13.7 5.6"/>
                      <path d="M17.5 3v3.8h-3.8M6.5 21v-3.8h3.8"/>
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="flex-shrink-0 text-accent-text">
                      <circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>
                    </svg>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-ui-text truncate">{formatFireTime(r)}</p>
                    <p className="text-xs text-ui-text3">{isRecurring ? 'Recurring' : 'One time'}</p>
                  </div>
                  {isActionable && (
                    <button
                      type="button"
                      onClick={() => cancelReminder(r.id)}
                      title="Remove"
                      className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-ui-text3/60 hover:text-danger-text hover:bg-danger/10 transition-colors">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                        <path d="M6 6l12 12M18 6 6 18"/>
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}

            {/* Add reminder — only for actionable meetings */}
            {isActionable && <div className="px-3 py-2">
              {showReminderInput ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="datetime-local"
                    value={reminderDt}
                    onChange={e => setReminderDt(e.target.value)}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    className="flex-1 h-6 text-micro border border-ui-border rounded-lg px-2 bg-ui-input text-ui-text focus:outline-none focus:ring-1 focus:ring-ui-ring/30"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!reminderDt) return;
                      await createReminder({ title: meeting.title, dtstart: new Date(reminderDt).toISOString(), source_entity_type: 'meeting', source_entity_id: meeting.meeting_id });
                      setReminderDt('');
                      setShowReminderInput(false);
                    }}
                    disabled={!reminderDt || creatingReminder}
                    className="flex-shrink-0 text-xs px-2 h-6 rounded-lg btn-sheen text-on-primary font-semibold disabled:opacity-50 transition-colors">
                    {creatingReminder ? '…' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowReminderInput(false); setReminderDt(''); }}
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-ui-text3 hover:text-ui-text hover:bg-ui-hover transition-colors">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setShowReminderInput(true)}
                  className="flex items-center gap-1.5 text-xs text-ui-text3 hover:text-ui-text transition-colors w-full">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add reminder
                </button>
              )}
            </div>}

          </div>
          </>
        )}
      </div>

      {/* ── Notes — header row + toggled form ─── */}
      <div className="space-y-1">
        {/* Header: pencil icon + count + "+" toggle */}
        <div className="flex items-center gap-1.5 px-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text flex-shrink-0">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
          <span className="text-xs font-medium text-ui-text flex-1">
            {notes.length > 0 ? `${notes.length} note${notes.length > 1 ? 's' : ''}` : 'Notes'}
          </span>
          <button
            title={showNoteForm ? 'Close' : 'Add note'}
            onClick={() => setShowNoteForm(v => !v)}
            className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${
              showNoteForm ? 'bg-accent/10 text-accent-text' : 'text-ui-text3/40 hover:text-accent-text hover:bg-accent/[0.06]'
            }`}
          >
            {showNoteForm ? (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            )}
          </button>
        </div>

        {/* Add note form */}
        {showNoteForm && (
          <div className="flex flex-col gap-1 bg-ui-subtle rounded-xl px-2 py-2">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Add a note…"
              rows={2}
              autoFocus
              className="w-full text-xs border border-ui-border rounded-lg px-2 py-1.5 bg-ui-card text-ui-text resize-none focus:outline-none focus:ring-1 focus:ring-ui-ring/30"
            />
            <div className="flex gap-1.5">
              <select
                value={noteCategory}
                onChange={e => setNoteCategory(e.target.value)}
                className="flex-1 text-xs h-6 border border-ui-border rounded-lg px-1.5 bg-ui-card text-ui-text focus:outline-none"
              >
                {NOTE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button
                onClick={async () => {
                  if (!noteText.trim()) return;
                  await addNote({ meetingId: meeting.meeting_id, content: noteText.trim(), category: noteCategory });
                  setNoteText('');
                  setNoteCategory('general');
                  setShowNoteForm(false);
                }}
                disabled={!noteText.trim() || addingNote}
                className="flex-shrink-0 text-xs px-2.5 h-6 rounded-lg btn-sheen text-on-primary font-semibold disabled:opacity-50 transition-colors"
              >
                {addingNote ? '…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* Notes list */}
        {notes.length > 0 && (
          <div className="space-y-1">
            {notes.map(n => (
              <div key={n.note_id} className="text-xs bg-ui-subtle rounded-lg px-2 py-1.5">
                <p className="text-ui-text leading-snug">{n.content}</p>
                <p className="text-ui-text3/50 mt-0.5">
                  {NOTE_CATEGORIES.find(c => c.value === n.category)?.label ?? n.category}
                  {n.author_name ? ` · ${n.author_name}` : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const IcoChevron = ({ open }: { open: boolean }) => <ExpandChevron open={open} />;

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  meeting: MeetingFull;
  onSelect?: (meetingId: string) => void;
  expandedId?: string | null;
  onExpand?: (id: string | null) => void;
  compact?: boolean;
  hideContextLink?: boolean;
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
  onDismissed?: (meetingId: string) => void;
}

const WorkspaceMeetingCard: React.FC<Props> = ({
  meeting,
  onSelect,
  expandedId,
  onExpand,
  compact = false,
  hideContextLink = false,
  onStartNoteTaker,
  onDismissed,
}) => {
  const navigate = useNavigate();
  const [updateStatus] = useUpdateMeetingStatusMutation();
  const [cancelMeeting] = useCancelMeetingMutation();
  const [showReschedule, setShowReschedule] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const ctxHref = meeting.context?.entity_id
    ? (ENTITY_ROUTE[meeting.context.context_type]?.(meeting.context.entity_id) ?? null)
    : null;

  useEffect(() => {
    if (!menuOpen) return;
    const onMouse = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node) && !menuBtnRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onScroll = () => setMenuOpen(false);
    document.addEventListener('mousedown', onMouse);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [menuOpen]);

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    const btn = menuBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuStyle({
        position: 'fixed', zIndex: POPOVER_Z, minWidth: 160,
        left: Math.min(rect.right - 160, window.innerWidth - 168),
        ...(spaceBelow < 150 ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    }
    setMenuOpen(v => !v);
  };

  // ── Expand state (controlled or local) ──────────────────────────────────────
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = onExpand !== undefined ? expandedId === meeting.meeting_id : localExpanded;
  const cardRef = useRef<HTMLDivElement>(null);

  // Report this meeting to SAM as active_meeting while its detail is expanded — same pattern as
  // TaskCard's active_task report; clears automatically (useSamSurfaceContext) on collapse/unmount.
  useSamSurfaceContext('meeting', isExpanded ? {
    meeting_id: meeting.meeting_id,
    title: meeting.title,
    status: meeting.status,
    meeting_type: meeting.meeting_type,
    start_time: meeting.start_time,
    context: meeting.context ? { display_label: meeting.context.display_label } : null,
  } : null);

  useEffect(() => {
    if (isExpanded && onExpand && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    if (!isExpanded) setShowReschedule(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const toggleExpand = () => {
    if (onExpand) {
      onExpand(isExpanded ? null : meeting.meeting_id);
    } else {
      setLocalExpanded(v => !v);
    }
  };

  // ── Swipe drag state ─────────────────────────────────────────────────────────
  const [dragX, setDragXState] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragXRef = useRef(0);
  const startX = useRef(0);
  const startY = useRef(0);
  const moved = useRef(false);
  const active = useRef(false);

  const setDragX = useCallback((x: number) => { dragXRef.current = x; setDragXState(x); }, []);

  const isActionable = ACTIONABLE.has(meeting.status);

  const resolveDrag = useCallback(() => {
    active.current = false;
    setDragging(false);
    const dx = dragXRef.current;
    if (dx >= THRESHOLD) {
      setDragX(180);
      setTimeout(() => { updateStatus({ id: meeting.meeting_id, status: 'completed' }).then(() => onDismissed?.(meeting.meeting_id)); setDragX(0); }, 290);
    } else if (dx <= -THRESHOLD) {
      setDragX(-180);
      setTimeout(() => { updateStatus({ id: meeting.meeting_id, status: 'cancelled' }).then(() => onDismissed?.(meeting.meeting_id)); setDragX(0); }, 290);
    } else {
      setDragX(0);
    }
  }, [meeting.meeting_id, updateStatus, setDragX, onDismissed]);


  // ── Mouse drag ───────────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    if (active.current) return;
    active.current = true;
    startX.current = e.clientX;
    startY.current = e.clientY;
    moved.current = false;
    setDragging(true);

    const onMove = (me: MouseEvent) => {
      const dx = me.clientX - startX.current;
      const dy = me.clientY - startY.current;
      if (!moved.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      moved.current = true;
      if (!isActionable) return;
      setDragX(Math.max(-130, Math.min(130, dx)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      resolveDrag();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [isActionable, resolveDrag, setDragX]);

  // ── Touch drag ───────────────────────────────────────────────────────────────
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (active.current) return;
    active.current = true;
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    moved.current = false;
    setDragging(true);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!active.current) return;
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    if (!moved.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
    moved.current = true;
    if (Math.abs(dy) > Math.abs(dx)) { resolveDrag(); return; }
    if (!isActionable) return;
    e.preventDefault();
    setDragX(Math.max(-130, Math.min(130, dx)));
  }, [isActionable, resolveDrag, setDragX]);

  const onTouchEnd = useCallback(() => { resolveDrag(); }, [resolveDrag]);

  // ── Click vs swipe ───────────────────────────────────────────────────────────
  const handleClick = () => {
    if (moved.current) { moved.current = false; return; }
    if (onSelect) {
      onSelect(meeting.meeting_id);
    } else {
      toggleExpand();
    }
  };

  // ── Date formatting ──────────────────────────────────────────────────────────
  const start = meeting.start_time ? new Date(meeting.start_time) : null;
  const end = meeting.end_time ? new Date(meeting.end_time) : null;
  const timeInfo = start ? getMeetingTimeDisplay(start, meeting.status) : null;
  const endStr = end ? end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : null;

  const sc = STATUS_CFG[meeting.status as keyof typeof STATUS_CFG]
    ?? { bg: 'bg-ui-subtle', text: 'text-ui-text2', label: meeting.status, bar: 'bg-ui-border3' };

  const rightP = dragX > 0 ? Math.min(1, dragX / THRESHOLD) : 0;
  const leftP = dragX < 0 ? Math.min(1, -dragX / THRESHOLD) : 0;

  return (
    <div ref={cardRef} className="relative rounded-2xl select-none touch-pan-y">

      {/* Swipe right → Complete background */}
      <SwipeHint
        side="left" inset={4} passThrough progress={rightP} tone="success" label="Complete"
        icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        }
      />

      {/* Swipe left → Cancel background */}
      <SwipeHint
        side="right" inset={4} passThrough progress={leftP} tone="danger" label="Cancel"
        icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        }
      />

      {/* Card */}
      <div
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleClick}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: dragging ? 'none' : 'transform 0.25s cubic-bezier(.22,1,.36,1)',
          willChange: 'transform',
        }}
        className={`card-workspace relative overflow-hidden cursor-pointer ${
          isExpanded ? 'border-accent/25 shadow-pop' : ''
        }`}
      >
        {/* Left status bar */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${sc.bar}`} />

        <div className={compact ? 'pl-3 pr-2 pt-1.5 pb-1.5 ml-2' : 'pl-4 pr-4 pt-2 pb-2 ml-2'}>

          {/* Row 1: icon + title + status badge + ⋮ menu */}
          <div className={`flex items-center gap-1.5 ${compact ? 'mb-1' : 'mb-1'}`}>
            <IcoCalendar className={`flex-shrink-0 ${sc.text} ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
            <h3 className={`flex-1 font-semibold text-ui-text leading-snug ${compact ? 'text-xs line-clamp-1' : 'text-sm line-clamp-1'}`}>
              {meeting.title}
            </h3>
            <span className={`flex-shrink-0 font-bold rounded-full uppercase tracking-wide ${sc.bg} ${sc.text} ${compact ? 'text-micro px-1.5 py-0.5' : 'text-micro px-2.5 py-1'}`}>
              {sc.label}
            </span>
            {isActionable && !compact && (
              <button
                ref={menuBtnRef}
                title="Actions"
                onMouseDown={e => e.stopPropagation()}
                onClick={openMenu}
                className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-colors btn-icon-hover ${menuOpen ? 'bg-ui-hover text-ui-text border-ui-border' : 'text-ui-text3 hover:bg-ui-hover'}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
                </svg>
              </button>
            )}
          </div>

          {/* Row 2: overdue tag + date chip + chevron */}
          <div className="flex items-center gap-2">
            {/* Overdue tag — kept alongside the date chip below (which also turns red), not in
                place of it: same separate, unmissable badge pattern as the Tasks card. */}
            {timeInfo?.isPast && (
              <span className={`flex-shrink-0 font-bold rounded-full uppercase tracking-wide bg-danger text-white ${compact ? 'text-micro px-1.5 py-0.5' : 'text-micro px-2.5 py-1'}`}>
                Overdue
              </span>
            )}
            {timeInfo && (
              <span className={`flex items-center gap-1 font-medium px-2 py-0.5 rounded-full border flex-shrink-0 min-w-0 ${
                compact ? 'text-xs' : 'text-xs'
              } ${
                timeInfo.isPast
                  ? 'bg-danger/10 border-danger/30 text-danger-text'
                  : timeInfo.main.toLowerCase().includes('today')
                    ? 'bg-accent-soft/30 border-accent/20 text-accent-text'
                    : 'bg-ui-subtle border-transparent text-ui-text3'
              }`}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {timeInfo.isPast ? (
                  <span className="truncate">{timeInfo.rel}</span>
                ) : (
                  <>
                    <span className="truncate">{timeInfo.main}{endStr ? ` – ${endStr}` : ''}</span>
                    {timeInfo.rel && <span className="opacity-60 flex-shrink-0"> · {timeInfo.rel}</span>}
                  </>
                )}
              </span>
            )}
            <span className="flex-1" />
            {!compact && <IcoChevron open={isExpanded} />}
          </div>


          {/* Row 3: context link pill — folded only; hidden when already inside the property view */}
          {!compact && !isExpanded && !hideContextLink && meeting.context?.display_label && (
            <div className="mt-1.5 flex">
              {ctxHref ? (
                <button
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); navigate(ctxHref); }}
                  className="inline-flex items-center gap-1 border border-accent/15 bg-accent-soft/20 hover:bg-accent-soft/40 rounded-full px-2 py-0.5 transition-colors max-w-full"
                >
                  <ContextTypeIcon type={meeting.context.context_type} />
                  <span className="text-xs text-accent-text/70 truncate">{meeting.context.display_label}</span>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-accent-text/40">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 border border-accent/10 bg-accent-soft/10 rounded-full px-2 py-0.5 max-w-full">
                  <ContextTypeIcon type={meeting.context.context_type} />
                  <span className="text-xs text-ui-text3 truncate">{meeting.context.display_label}</span>
                </span>
              )}
            </div>
          )}

          {/* Inline expanded detail */}
          {isExpanded && (
            <div className="mt-1.5">
              <MeetingInlineDetail
                meeting={meeting}
                showReschedule={showReschedule}
                setShowReschedule={setShowReschedule}
                onStartNoteTaker={onStartNoteTaker}
              />
            </div>
          )}
        </div>
      </div>

      {/* Action menu portal — fixed to viewport, unaffected by scroll or overflow */}
      {menuOpen && createPortal(
        <div ref={menuRef} style={menuStyle}
          className="rounded-xl border border-ui-border bg-ui-card shadow-pop overflow-hidden py-0.5">
          <button type="button" onMouseDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); updateStatus({ id: meeting.meeting_id, status: 'completed' }).then(() => onDismissed?.(meeting.meeting_id)); if (isExpanded) toggleExpand(); setMenuOpen(false); }}
            className="flex w-full items-center gap-1.5 px-3 h-[18px] text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success-text flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
            Complete
          </button>
          <button type="button" onMouseDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); updateStatus({ id: meeting.meeting_id, status: 'no_show' }).then(() => onDismissed?.(meeting.meeting_id)); if (isExpanded) toggleExpand(); setMenuOpen(false); }}
            className="flex w-full items-center gap-1.5 px-3 h-[18px] text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-warning-text flex-shrink-0">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <line x1="17" y1="11" x2="23" y2="17"/><line x1="23" y1="11" x2="17" y2="17"/>
            </svg>
            No Show
          </button>
          <button type="button" onMouseDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); setShowReschedule(v => !v); if (!isExpanded) toggleExpand(); setMenuOpen(false); }}
            className="flex w-full items-center gap-1.5 px-3 h-[18px] text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
              <path d="M17 14.5a4 4 0 1 0-1 3.46"/><polyline points="17 19 17 14.5 13 14.5"/>
            </svg>
            Reschedule
          </button>
          <button type="button" onMouseDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); cancelMeeting({ id: meeting.meeting_id }).then(() => onDismissed?.(meeting.meeting_id)); if (isExpanded) toggleExpand(); setMenuOpen(false); }}
            className="flex w-full items-center gap-1.5 px-3 h-[18px] text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-danger-text flex-shrink-0">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Cancel
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default WorkspaceMeetingCard;
